/**
 * The warehouse catalog (prompt §1, Phase 2 plan §2.1 and W1): categories, items, their purchase
 * units and transfer prices. Master data, edited online only through the API (plan §14.1); PCs
 * receive it through the sync feed. Every request schema here is shared by the API and the screens,
 * so a field is refused with the same words in both (D93).
 */
import { z } from 'zod';

import { latinDigits, stripInvisible } from '../network/address.ts';
import { DISPLAY_FACTOR, STOCK_UNITS, type StockUnit } from './quantities.ts';

/**
 * Where a category's goods are costed when a branch consumes them (plan §10.7): the chart's
 * system keys 5100 food, 5200 packaging, 6700 cleaning, 6900 other.
 */
export const CONSUMPTION_ACCOUNTS = [
  'food_cost',
  'packaging_cost',
  'cleaning',
  'other_expenses',
] as const;
export type ConsumptionAccount = (typeof CONSUMPTION_ACCOUNTS)[number];

export const CATALOG_CODE_MAX = 30;
export const CATALOG_NAME_MAX = 80;
export const ITEM_NOTES_MAX = 500;
export const MAX_UNITS_PER_ITEM = 10;
export const SHELF_LIFE_DAYS_MAX = 3650;
export const EXPIRY_ALERT_DAYS_MAX = 365;
export const PRICE_REASON_MAX = 300;
/** The largest whole quantity or amount the database and JavaScript both keep exactly. */
const SAFE = Number.MAX_SAFE_INTEGER;

const CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]*$/;

/** A code as typed (Arabic digits, lower case and spaces around it accepted) → `BEEF-01`. */
export const catalogCodeSchema = z.string().transform((value, ctx) => {
  const code = latinDigits(stripInvisible(value)).trim().toUpperCase();
  if (code === '') {
    ctx.addIssue({ code: 'custom', message: 'required', params: { dobCode: 'required' } });
    return z.NEVER;
  }
  if (code.length > CATALOG_CODE_MAX || !CODE_PATTERN.test(code)) {
    ctx.addIssue({
      code: 'custom',
      message: 'item_code_format',
      params: { dobCode: 'item_code_format' },
    });
    return z.NEVER;
  }
  return code;
});

const nameSchema = z.string().trim().min(1).max(CATALOG_NAME_MAX);
const stockUnitSchema = z.enum(STOCK_UNITS);

// ── Categories ───────────────────────────────────────────────────────────────

export const CategoryFieldsSchema = z.strictObject({
  code: catalogCodeSchema,
  nameAr: nameSchema,
  nameEn: nameSchema,
  consumptionAccount: z.enum(CONSUMPTION_ACCOUNTS).default('food_cost'),
  sortOrder: z.number().int().min(0).max(9999).default(0),
});
export type CategoryFields = z.infer<typeof CategoryFieldsSchema>;
export type CategoryFieldsInput = z.input<typeof CategoryFieldsSchema>;

export const CategoryPatchSchema = z
  .strictObject({
    code: catalogCodeSchema.optional(),
    nameAr: nameSchema.optional(),
    nameEn: nameSchema.optional(),
    consumptionAccount: z.enum(CONSUMPTION_ACCOUNTS).optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    isActive: z.boolean().optional(),
  })
  .refine((p) => Object.values(p).some((v) => v !== undefined), { message: 'Nothing to change' });
export type CategoryPatch = z.infer<typeof CategoryPatchSchema>;

// ── Purchase units ───────────────────────────────────────────────────────────

/**
 * A purchase unit: a whole number of stock units (box = 24 pieces, sack = 25,000 g). At least 2 —
 * the stock unit itself (1 piece, 1 g) is always there and is never a separate row.
 */
export const ItemUnitFieldsSchema = z.strictObject({
  nameAr: nameSchema,
  nameEn: nameSchema,
  factor: z.number().int().min(2).max(SAFE),
  isDefaultPurchase: z.boolean().default(false),
});
export type ItemUnitFields = z.infer<typeof ItemUnitFieldsSchema>;
export type ItemUnitFieldsInput = z.input<typeof ItemUnitFieldsSchema>;

/** A unit's factor never changes (old documents keep their meaning): retire it and add another. */
export const ItemUnitPatchSchema = z
  .strictObject({
    nameAr: nameSchema.optional(),
    nameEn: nameSchema.optional(),
    isDefaultPurchase: z.boolean().optional(),
    isActive: z.boolean().optional(),
  })
  .refine((p) => Object.values(p).some((v) => v !== undefined), { message: 'Nothing to change' });
export type ItemUnitPatch = z.infer<typeof ItemUnitPatchSchema>;

// ── Items ────────────────────────────────────────────────────────────────────

const itemFlags = {
  tracksBatches: z.boolean(),
  shelfLifeDays: z.number().int().min(1).max(SHELF_LIFE_DAYS_MAX).nullable(),
  expiryAlertDays: z.number().int().min(0).max(EXPIRY_ALERT_DAYS_MAX).nullable(),
  isPurchased: z.boolean(),
  isProduced: z.boolean(),
  isSellableToBranches: z.boolean(),
  isCountableForRecipes: z.boolean(),
  notes: z.string().trim().max(ITEM_NOTES_MAX).nullable(),
};

/** A new item with its purchase units. The stock unit is chosen once and never changes. */
export const ItemFieldsSchema = z
  .strictObject({
    code: catalogCodeSchema,
    nameAr: nameSchema,
    nameEn: nameSchema,
    categoryId: z.uuid(),
    stockUnit: stockUnitSchema,
    tracksBatches: itemFlags.tracksBatches.default(false),
    shelfLifeDays: itemFlags.shelfLifeDays.default(null),
    expiryAlertDays: itemFlags.expiryAlertDays.default(null),
    isPurchased: itemFlags.isPurchased.default(true),
    isProduced: itemFlags.isProduced.default(false),
    isSellableToBranches: itemFlags.isSellableToBranches.default(true),
    isCountableForRecipes: itemFlags.isCountableForRecipes.default(false),
    notes: itemFlags.notes.default(null),
    units: z.array(ItemUnitFieldsSchema).max(MAX_UNITS_PER_ITEM).default([]),
  })
  .superRefine((item, ctx) => {
    const defaults = item.units.filter((u) => u.isDefaultPurchase).length;
    if (defaults > 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['units'],
        message: 'one default purchase unit at most',
        params: { dobCode: 'invalid' },
      });
    }
    const names = new Set<string>();
    item.units.forEach((unit, index) => {
      const key = unit.nameAr.toLowerCase();
      if (names.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['units', index, 'nameAr'],
          message: 'unit name repeated',
          params: { dobCode: 'invalid' },
        });
      }
      names.add(key);
    });
  });
export type ItemFields = z.infer<typeof ItemFieldsSchema>;
export type ItemFieldsInput = z.input<typeof ItemFieldsSchema>;

/** Changes to an item: everything but the stock unit, and at least one field. */
export const ItemPatchSchema = z
  .strictObject({
    code: catalogCodeSchema.optional(),
    nameAr: nameSchema.optional(),
    nameEn: nameSchema.optional(),
    categoryId: z.uuid().optional(),
    tracksBatches: itemFlags.tracksBatches.optional(),
    shelfLifeDays: itemFlags.shelfLifeDays.optional(),
    expiryAlertDays: itemFlags.expiryAlertDays.optional(),
    isPurchased: itemFlags.isPurchased.optional(),
    isProduced: itemFlags.isProduced.optional(),
    isSellableToBranches: itemFlags.isSellableToBranches.optional(),
    isCountableForRecipes: itemFlags.isCountableForRecipes.optional(),
    notes: itemFlags.notes.optional(),
    isActive: z.boolean().optional(),
  })
  .refine((p) => Object.values(p).some((v) => v !== undefined), { message: 'Nothing to change' });
export type ItemPatch = z.infer<typeof ItemPatchSchema>;

/** An item's minimum level and whether a location keeps it (per location; quantity in stock units). */
export const ItemLocationSettingSchema = z.strictObject({
  minQuantity: z.number().int().min(0).max(SAFE).nullable(),
  isStocked: z.boolean(),
});
export type ItemLocationSetting = z.infer<typeof ItemLocationSettingSchema>;

// ── Transfer prices ──────────────────────────────────────────────────────────

/**
 * A new transfer price (D247). `perUnitId` null = per display unit (1 piece, 1 kg, 1 L);
 * otherwise per that purchase unit (a box of 24). `effectiveFrom` null = now; otherwise a time
 * from now to one year ahead — never back-dated (checked again by the API and the database).
 */
export const ItemPriceChangeSchema = z.strictObject({
  price: z.number().int().min(0).max(SAFE),
  perUnitId: z.uuid().nullable().default(null),
  effectiveFrom: z.iso.datetime({ offset: true }).nullable().default(null),
  reason: z.string().trim().min(5).max(PRICE_REASON_MAX),
});
export type ItemPriceChange = z.infer<typeof ItemPriceChangeSchema>;
export type ItemPriceChangeInput = z.input<typeof ItemPriceChangeSchema>;

/** Stock units a price "per display unit" is for: 1 piece, 1,000 g, 1,000 ml. */
export function displayPerQuantity(unit: StockUnit): number {
  return DISPLAY_FACTOR[unit];
}
